import { useAuth } from '../lib/auth';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Landmark, ShieldAlert } from 'lucide-react';
import { TefCuentasTab } from '../components/tef/TefCuentasTab';
import { TefBancosTab } from '../components/tef/TefBancosTab';
import { TefConfigEmisorTab } from '../components/tef/TefConfigEmisorTab';
import { TefColaboradoresTab } from '../components/tef/TefColaboradoresTab';

/**
 * Configuración TEF Banco de Chile (Admin/Gerencia).
 * Mantenedor de las cuentas bancarias de proveedores, la tabla de bancos (CMF)
 * y los datos del emisor Chirino usados para generar el archivo .txt de pagos.
 *
 * Iter 308.j (Renzo, ago-2026) — agrega tab "Cuentas de Colaboradores"
 * visible para Admin / Gerencia / RRHH Encargado (read-only para RRHH).
 */
export default function TefConfigPage() {
  const { user } = useAuth();
  const rol = (user?.rol || '').toLowerCase();
  const canEditTef = ['admin', 'administrador', 'gerencia'].includes(rol);
  const canViewColaboradores = canEditTef || rol === 'rrhh_encargado';

  if (!canEditTef && !canViewColaboradores) {
    return (
      <div className="p-6" data-testid="tef-config-denied">
        <Card className="max-w-lg mx-auto border-amber-300">
          <CardContent className="flex items-start gap-3 p-6">
            <ShieldAlert className="h-6 w-6 text-amber-600 shrink-0" />
            <div>
              <h2 className="font-semibold">Acceso restringido</h2>
              <p className="text-sm text-muted-foreground">
                Solo Administrador, Gerencia o RRHH Encargado pueden ver esta sección.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // RRHH Encargado → sólo ve Colaboradores (tab default)
  const defaultTab = canEditTef ? 'cuentas' : 'colaboradores';

  return (
    <div className="space-y-4 p-2 md:p-4" data-testid="tef-config-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Landmark className="w-6 h-6 text-indigo-600" />
          Cuentas Bancarias de Proveedores · Banco de Chile
        </h1>
        <p className="text-sm text-muted-foreground">
          Mantén las cuentas bancarias de proveedores, la tabla de bancos (CMF) y los datos del
          emisor Chirino que se usan al generar el archivo <code>.txt</code> de transferencias.
        </p>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList data-testid="tef-tabs">
          {canEditTef && (
            <TabsTrigger value="cuentas" data-testid="tab-cuentas">Cuentas de proveedores</TabsTrigger>
          )}
          {canViewColaboradores && (
            <TabsTrigger value="colaboradores" data-testid="tab-colaboradores">Cuentas de Colaboradores</TabsTrigger>
          )}
          {canEditTef && (
            <TabsTrigger value="bancos" data-testid="tab-bancos">Datos Banco (CMF)</TabsTrigger>
          )}
          {canEditTef && (
            <TabsTrigger value="emisor" data-testid="tab-emisor">Emisor (Chirino)</TabsTrigger>
          )}
        </TabsList>
        {canEditTef && (
          <TabsContent value="cuentas" className="mt-4">
            <TefCuentasTab />
          </TabsContent>
        )}
        {canViewColaboradores && (
          <TabsContent value="colaboradores" className="mt-4">
            <TefColaboradoresTab />
          </TabsContent>
        )}
        {canEditTef && (
          <TabsContent value="bancos" className="mt-4">
            <TefBancosTab />
          </TabsContent>
        )}
        {canEditTef && (
          <TabsContent value="emisor" className="mt-4">
            <TefConfigEmisorTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
